import './AnnotationToolsList.scss';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { AddMarkingToolButtonContainer } from '../../../../../../modules/projects/details/annotationTools/markingTools/add/ui/button/AddMarkingToolButton.container';
import { AddMarkingToolModalContainer } from '../../../../../../modules/projects/details/annotationTools/markingTools/add/ui/modal/AddMarkingToolModal.container';
import { EditMarkingToolButtonContainer } from '../../../../../../modules/projects/details/annotationTools/markingTools/edit/ui/button/EditMarkingToolButton.container';
import { EditMarkingToolModalContainer } from '../../../../../../modules/projects/details/annotationTools/markingTools/edit/ui/modal/EditMarkingToolModal.container';
import { GTable } from '../../../../../components/table/GTable';
import { IGColumnProps } from '../../../../../components/table/IGColumnProps';
import { getContrastingColor } from '../../../../../helpers/color.helpers';
import { useProjectDetailsPermissions } from '../../../projectDetails.permissions';
import { IProjectDetailsStore } from '../../../projectDetails.store';
import { AnnotationTypeDeleteContainer } from '../containers/AnnotationTypeDeleteContainer';
import { IAnnotationType } from '../projectDetailsTools.models';

type AnnotationTypeWithScopeUsage = IAnnotationType & { isUsedAsScope: boolean };

interface IAnnotationTypesListProps {
  list: AnnotationTypeWithScopeUsage[];
  isLoading: boolean;
  onOrderChange: (annotationTypeId: string, to: number) => Promise<void>;
}

export const AnnotationTypesList = observer((props: IAnnotationTypesListProps): ReactElement => {
  const { t } = useTranslator();
  const projectDetailsPermissions = useProjectDetailsPermissions();
  const projectDetailsStore = useInjection(IProjectDetailsStore.$);
  const canEdit = projectDetailsPermissions.canEditProject(projectDetailsStore.status);

  const columns: IGColumnProps<AnnotationTypeWithScopeUsage>[] = [
    {
      sortBy: 'name',
      headerName: t('name'),
      renderer: (x) => x.name,
    },
    {
      sortBy: 'selectorType',
      headerName: t('tool_type'),
      renderer: typeRenderer,
    },
    {
      sortBy: 'color',
      headerName: t('color'),
      renderer: (value) => (
        <div
          className='color-fill'
          css={{ backgroundColor: value.color, color: getContrastingColor(value.color) }}
        >
          {value.color}
        </div>
      ),
      cellClassName: 'cell-with-bg-color',
    },
    {
      headerName: t('actions'),
      actions: true,
      width: 'min-content',
      renderer: actionsRenderer,
    },
  ];

  if (canEdit.allowed) {
    columns.unshift({
      headerName: '',
      isDragHandle: true,
      renderer: (): ReactElement => <FontAwesomeIcon icon={faBars} />,
    });
  }

  function typeRenderer(data: AnnotationTypeWithScopeUsage): ReactElement {
    return <span>{t(data.selectorType)}</span>;
  }

  function actionsRenderer(data: AnnotationTypeWithScopeUsage): ReactElement {
    return (
      <>
        <EditMarkingToolButtonContainer markingToolId={data.id} />
        <AnnotationTypeDeleteContainer
          annotationType={data}
          isUsedAsScope={data.isUsedAsScope}
        />
      </>
    );
  }

  function handleRowDrag(cb: (annotationTypeId: string, to: number) => Promise<void>) {
    return async (from: number, to: number): Promise<void> => {
      await cb(props.list[from].id, to);
    };
  }

  return (
    <div className='annotation-tools-list'>
      <div className='annotation-tools-list-header'>
        <div>{t('segmentation')}</div>
        {canEdit.allowed && (
          <>
            <AddMarkingToolButtonContainer />
            <AddMarkingToolModalContainer />
          </>
        )}
      </div>
      <GTable
        isLoading={props.isLoading}
        columns={columns}
        items={props.list}
        enableDrag={canEdit.allowed}
        onRowDragEnd={handleRowDrag(props.onOrderChange)}
        noItemsPlaceholder={t('no_tools_have_been_added_yet')}
      />
      <EditMarkingToolModalContainer />
    </div>
  );
});
